import { useCallback, useMemo } from 'react';
import { useNostr } from '@nostrify/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { nip44, nip19, generateSecretKey, finalizeEvent, getEventHash } from 'nostr-tools';

import type { NostrEvent } from '@nostrify/nostrify';
import { useNostrLogin } from '@nostrify/react/login';
import { useCurrentUser } from './useCurrentUser';
import { useAppContext } from './useAppContext';
import { hasNip44 as checkNip44, isInteractiveSigner } from '@/lib/signerCapabilities';

/**
 * React Query options that keep an interactive signer (Amber) from being stormed
 * with decrypt popups. Each DM query decrypts up to hundreds of gift wraps, and
 * every decrypt is an Amber intent — so with Amber we must NOT poll and must NOT
 * re-run on window focus (Amber handing focus back would re-trigger the query
 * and pop Amber again). With a silent signer (extension/nsec/bunker) we keep the
 * live polling behaviour. Callers spread this over their base options.
 */
function amberSafeQueryOptions(interactive: boolean) {
  return {
    refetchOnWindowFocus: false as const,
    // No background polling for Amber (each poll = a decrypt storm). Silent
    // signers keep a 60s poll for near-live updates.
    refetchInterval: interactive ? (false as const) : 60000,
    // Long stale time for Amber so navigating back to a chat doesn't re-decrypt.
    staleTime: interactive ? 5 * 60 * 1000 : 30000,
  };
}

// NIP-17 Message Types
export interface DirectMessage {
  id: string;
  pubkey: string; // Sender's pubkey
  content: string;
  createdAt: number;
  replyTo?: string; // Parent message ID
  conversationWith: string; // The other party's pubkey (for 1:1) or group ID (for groups)
  participants?: string[]; // All participants in group chat
  subject?: string; // Optional conversation subject/title
}

export interface Conversation {
  pubkey: string; // The other party (1:1) or empty for groups
  participants?: string[]; // All participants (for groups)
  lastMessage?: DirectMessage;
  unreadCount: number;
  isGroup?: boolean;
  subject?: string;
}

// Group chat identifier is hash of sorted participants
export function getGroupId(participants: string[]): string {
  const sorted = [...participants].sort();
  return sorted.join(':');
}

// Constants
const GIFT_WRAP_KIND = 1059;
const SEAL_KIND = 13;
const DM_KIND = 14;
const TWO_DAYS = 2 * 24 * 60 * 60;

// Helper to generate random timestamp within past 2 days
const randomPastTimestamp = () => Math.floor(Date.now() / 1000) - Math.floor(Math.random() * TWO_DAYS);

/**
 * Create an unsigned rumor event (NIP-17 kind 14)
 * Supports both 1:1 and group conversations
 */
function createRumor(
  senderPubkey: string,
  recipientPubkeys: string | string[],
  content: string,
  options?: {
    replyTo?: string;
    subject?: string;
  }
): NostrEvent {
  const recipients = Array.isArray(recipientPubkeys) ? recipientPubkeys : [recipientPubkeys];
  
  const tags: string[][] = recipients.map(pubkey => ['p', pubkey]);

  if (options?.replyTo) {
    tags.push(['e', options.replyTo]);
  }
  
  if (options?.subject) {
    tags.push(['subject', options.subject]);
  }

  const rumor = {
    pubkey: senderPubkey,
    created_at: Math.floor(Date.now() / 1000),
    kind: DM_KIND,
    tags,
    content,
  };

  // Calculate id but don't sign
  const id = getEventHash(rumor as NostrEvent);

  return {
    ...rumor,
    id,
    sig: '', // Unsigned
  } as NostrEvent;
}

/**
 * Hook to send NIP-17 direct messages (1:1 or group)
 */
export function useSendDirectMessage() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { config } = useAppContext();
  const queryClient = useQueryClient();

  const sendMessage = useCallback(async (
    recipientPubkeys: string | string[],
    content: string,
    options?: {
      replyTo?: string;
      subject?: string;
    }
  ): Promise<void> => {
    if (!user?.signer?.nip44) {
      throw new Error('NIP-44 encryption not available. Please use a compatible signer.');
    }

    const recipients = Array.isArray(recipientPubkeys) ? recipientPubkeys : [recipientPubkeys];
    const isGroup = recipients.length > 1;

    // 1. Create the unsigned rumor (kind 14)
    const rumor = createRumor(user.pubkey, recipients, content, options);

    // 2. For each recipient, create a seal encrypted to them
    // In NIP-17, we encrypt the rumor to each recipient separately
    const createSealAndWrap = async (targetPubkey: string): Promise<NostrEvent> => {
      // Encrypt rumor to this specific recipient using NIP-44
      const rumorJson = JSON.stringify(rumor);
      const encryptedRumor = await user.signer.nip44!.encrypt(targetPubkey, rumorJson);

      const sealEvent = await user.signer.signEvent({
        kind: SEAL_KIND,
        created_at: randomPastTimestamp(),
        tags: [],
        content: encryptedRumor,
      });

      // Generate ephemeral secret key for the gift wrap (finalizeEvent derives
      // the matching pubkey internally).
      const ephemeralSecretKey = generateSecretKey();

      // Encrypt seal to target using ephemeral key.
      // getConversationKey expects the raw secret key bytes (Uint8Array).
      const sealJson = JSON.stringify(sealEvent);
      const conversationKey = nip44.v2.utils.getConversationKey(
        ephemeralSecretKey,
        targetPubkey
      );
      const encryptedSeal = nip44.v2.encrypt(sealJson, conversationKey);

      // Create gift wrap event and sign with ephemeral key.
      // finalizeEvent takes an EventTemplate (no pubkey — it derives it from
      // the secret key) and returns a fully-signed event.
      return finalizeEvent({
        created_at: randomPastTimestamp(),
        kind: GIFT_WRAP_KIND,
        tags: [['p', targetPubkey]],
        content: encryptedSeal,
      }, ephemeralSecretKey) as NostrEvent;
    };

    // 3. Create gift wraps for all recipients AND sender (so sender sees own messages)
    const allTargets = [...new Set([...recipients, user.pubkey])];
    const giftWraps = await Promise.all(allTargets.map(createSealAndWrap));

    // 4. Publish to relays
    const writeRelays = config.relayMetadata.relays
      .filter(r => r.write)
      .map(r => r.url);

    // Publish all gift wraps
    for (const relayUrl of writeRelays) {
      try {
        const relay = nostr.relay(relayUrl);
        for (const wrap of giftWraps) {
          await relay.event(wrap);
        }
      } catch (error) {
        console.warn(`[DM] Failed to publish to ${relayUrl}:`, error);
      }
    }

    // Invalidate queries to refresh message list
    queryClient.invalidateQueries({ queryKey: ['direct-messages'] });
    queryClient.invalidateQueries({ queryKey: ['conversations'] });
    if (isGroup) {
      queryClient.invalidateQueries({ queryKey: ['group-conversations'] });
    }

  }, [user, nostr, config, queryClient]);

  return useMutation({
    mutationFn: async ({ recipients, content, replyTo, subject }: {
      recipients: string | string[];
      content: string;
      replyTo?: string;
      subject?: string;
    }) => {
      await sendMessage(recipients, content, { replyTo, subject });
    },
  });
}

/**
 * Hook to send group DMs for P2P trading
 * Convenience wrapper with trading-specific defaults
 */
export function useSendTradeDM() {
  const sendDM = useSendDirectMessage();
  
  return useMutation({
    mutationFn: async ({ 
      participants, 
      content, 
      itemTitle,
      tradeType 
    }: {
      participants: string[];
      content: string;
      itemTitle?: string;
      tradeType?: 'buy' | 'sell' | 'trade';
    }) => {
      const subject = itemTitle 
        ? `${tradeType === 'buy' ? 'WTB' : tradeType === 'sell' ? 'For Sale' : 'Trade'}: ${itemTitle}`
        : undefined;
      
      await sendDM.mutateAsync({ 
        recipients: participants, 
        content,
        subject 
      });
    },
  });
}

/**
 * Hook to fetch and decrypt direct messages for a conversation
 */
export function useConversation(otherPubkey: string) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { logins } = useNostrLogin();
  const { config } = useAppContext();
  const nip44Available = checkNip44(user, logins);

  const relayUrls = config.relayMetadata.relays
    .filter(r => r.read)
    .map(r => r.url)
    .sort()
    .join(',');

  return useQuery({
    queryKey: ['direct-messages', otherPubkey, user?.pubkey, relayUrls],
    queryFn: async (c) => {
      if (!user?.signer?.nip44 || !otherPubkey) return [];

      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(15000)]);

      const readRelays = config.relayMetadata.relays
        .filter(r => r.read)
        .map(r => r.url);

      // Query for gift wraps addressed to us
      const allEvents: NostrEvent[] = [];

      for (const relayUrl of readRelays) {
        try {
          const relay = nostr.relay(relayUrl);
          const events = await relay.query(
            [{
              kinds: [GIFT_WRAP_KIND],
              '#p': [user.pubkey],
              limit: 100,
            }],
            { signal }
          );
          allEvents.push(...events);
        } catch (error) {
          console.warn(`[DM] Failed to query ${relayUrl}:`, error);
        }
      }

      // Deduplicate by ID
      const uniqueEvents = Array.from(
        new Map(allEvents.map(e => [e.id, e])).values()
      );

      // Decrypt and filter messages
      const messages: DirectMessage[] = [];

      for (const giftWrap of uniqueEvents) {
        try {
          // Decrypt gift wrap to get seal
          const sealJson = await user.signer.nip44.decrypt(giftWrap.pubkey, giftWrap.content);
          const seal = JSON.parse(sealJson) as NostrEvent;

          // Verify seal is kind 13
          if (seal.kind !== SEAL_KIND) continue;

          // Decrypt seal to get rumor
          const rumorJson = await user.signer.nip44.decrypt(seal.pubkey, seal.content);
          const rumor = JSON.parse(rumorJson) as NostrEvent;

          // Verify rumor pubkey matches seal pubkey (prevent impersonation)
          if (rumor.pubkey !== seal.pubkey) continue;

          // Verify this is a kind 14 DM
          if (rumor.kind !== DM_KIND) continue;

          // Check if this message is part of the conversation with otherPubkey
          const pTags = rumor.tags.filter(t => t[0] === 'p').map(t => t[1]);

          // 1:1 only. A rumor with more than one recipient is a GROUP message
          // (handled by useGroupConversation). Without this guard a group chat
          // leaks into the 1:1 thread with each of its participants.
          if (pTags.length > 1) continue;

          const isFromOther = rumor.pubkey === otherPubkey;
          const isToOther = pTags.includes(otherPubkey);
          const isFromMe = rumor.pubkey === user.pubkey;
          const isToMe = pTags.includes(user.pubkey);

          // Message is part of this conversation if:
          // - It's from the other person to me, OR
          // - It's from me to the other person
          if ((isFromOther && isToMe) || (isFromMe && isToOther)) {
            const replyTo = rumor.tags.find(t => t[0] === 'e')?.[1];

            messages.push({
              id: rumor.id,
              pubkey: rumor.pubkey,
              content: rumor.content,
              createdAt: rumor.created_at,
              replyTo,
              conversationWith: otherPubkey,
            });
          }
        } catch (error) {
          // Failed to decrypt - not for us or corrupted
          console.debug('[DM] Failed to decrypt message:', error);
        }
      }

      // Sort by creation time (oldest first for chat display)
      return messages.sort((a, b) => a.createdAt - b.createdAt);
    },
    enabled: nip44Available && !!otherPubkey,
    ...amberSafeQueryOptions(isInteractiveSigner(user, logins)),
  });
}

/**
 * Hook to fetch all conversations
 */
export function useConversations() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { logins } = useNostrLogin();
  const { config } = useAppContext();
  const nip44Available = checkNip44(user, logins);

  const relayUrls = config.relayMetadata.relays
    .filter(r => r.read)
    .map(r => r.url)
    .sort()
    .join(',');

  return useQuery({
    queryKey: ['conversations', user?.pubkey, relayUrls],
    queryFn: async (c) => {
      if (!user?.signer?.nip44) return [];

      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(15000)]);

      const readRelays = config.relayMetadata.relays
        .filter(r => r.read)
        .map(r => r.url);

      // Query for gift wraps addressed to us
      const allEvents: NostrEvent[] = [];

      for (const relayUrl of readRelays) {
        try {
          const relay = nostr.relay(relayUrl);
          const events = await relay.query(
            [{
              kinds: [GIFT_WRAP_KIND],
              '#p': [user.pubkey],
              limit: 200,
            }],
            { signal }
          );
          allEvents.push(...events);
        } catch (error) {
          console.warn(`[DM] Failed to query ${relayUrl}:`, error);
        }
      }

      // Deduplicate
      const uniqueEvents = Array.from(
        new Map(allEvents.map(e => [e.id, e])).values()
      );

      // Decrypt and organize by conversation
      const conversationMap = new Map<string, DirectMessage[]>();

      for (const giftWrap of uniqueEvents) {
        try {
          // Decrypt gift wrap
          const sealJson = await user.signer.nip44.decrypt(giftWrap.pubkey, giftWrap.content);
          const seal = JSON.parse(sealJson) as NostrEvent;

          if (seal.kind !== SEAL_KIND) continue;

          // Decrypt seal
          const rumorJson = await user.signer.nip44.decrypt(seal.pubkey, seal.content);
          const rumor = JSON.parse(rumorJson) as NostrEvent;

          if (rumor.pubkey !== seal.pubkey) continue;
          if (rumor.kind !== DM_KIND) continue;

          // Determine conversation partner
          const pTags = rumor.tags.filter(t => t[0] === 'p').map(t => t[1]);

          // 1:1 list only. A rumor addressed to more than one recipient is a
          // GROUP message (surfaced by useGroupConversations). Without this
          // guard each group would masquerade as a phantom 1:1 with whichever
          // participant happened to speak, and would appear twice in the UI.
          if (pTags.length > 1) continue;

          let conversationWith: string;

          if (rumor.pubkey === user.pubkey) {
            // Message from me - conversation is with the recipient
            conversationWith = pTags.find(p => p !== user.pubkey) || pTags[0];
          } else {
            // Message to me - conversation is with the sender
            conversationWith = rumor.pubkey;
          }

          if (!conversationWith) continue;

          const message: DirectMessage = {
            id: rumor.id,
            pubkey: rumor.pubkey,
            content: rumor.content,
            createdAt: rumor.created_at,
            conversationWith,
          };

          const existing = conversationMap.get(conversationWith) || [];
          existing.push(message);
          conversationMap.set(conversationWith, existing);

        } catch (error) {
          console.debug('[DM] Failed to decrypt:', error);
        }
      }

      // Build conversation list
      const conversations: Conversation[] = [];

      for (const [pubkey, messages] of conversationMap) {
        // Sort messages to find the latest
        const sorted = messages.sort((a, b) => b.createdAt - a.createdAt);
        
        conversations.push({
          pubkey,
          lastMessage: sorted[0],
          unreadCount: 0, // Could implement read receipts later
        });
      }

      // Sort conversations by last message time
      return conversations.sort((a, b) => 
        (b.lastMessage?.createdAt || 0) - (a.lastMessage?.createdAt || 0)
      );
    },
    enabled: nip44Available,
    ...amberSafeQueryOptions(isInteractiveSigner(user, logins)),
  });
}

/**
 * Helper to format pubkey for display
 */
export function formatPubkeyShort(pubkey: string): string {
  try {
    const npub = nip19.npubEncode(pubkey);
    return `${npub.slice(0, 8)}...${npub.slice(-4)}`;
  } catch {
    return `${pubkey.slice(0, 8)}...${pubkey.slice(-4)}`;
  }
}

/**
 * Hook to fetch group conversations (multi-participant chats)
 * Group conversations have more than 1 p-tag in the rumor
 */
export function useGroupConversations() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { logins } = useNostrLogin();
  const { config } = useAppContext();
  const nip44Available = checkNip44(user, logins);

  const relayUrls = config.relayMetadata.relays
    .filter(r => r.read)
    .map(r => r.url)
    .sort()
    .join(',');

  return useQuery({
    queryKey: ['group-conversations', user?.pubkey, relayUrls],
    queryFn: async (c) => {
      if (!user?.signer?.nip44) return [];

      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(15000)]);

      const readRelays = config.relayMetadata.relays
        .filter(r => r.read)
        .map(r => r.url);

      // Query for gift wraps addressed to us
      const allEvents: NostrEvent[] = [];

      for (const relayUrl of readRelays) {
        try {
          const relay = nostr.relay(relayUrl);
          const events = await relay.query(
            [{
              kinds: [GIFT_WRAP_KIND],
              '#p': [user.pubkey],
              limit: 200,
            }],
            { signal }
          );
          allEvents.push(...events);
        } catch (error) {
          console.warn(`[DM] Failed to query ${relayUrl}:`, error);
        }
      }

      // Deduplicate
      const uniqueEvents = Array.from(
        new Map(allEvents.map(e => [e.id, e])).values()
      );

      // Decrypt and find group conversations (>1 participant)
      const groupMap = new Map<string, { participants: string[]; messages: DirectMessage[]; subject?: string }>();

      for (const giftWrap of uniqueEvents) {
        try {
          // Decrypt gift wrap
          const sealJson = await user.signer.nip44.decrypt(giftWrap.pubkey, giftWrap.content);
          const seal = JSON.parse(sealJson) as NostrEvent;

          if (seal.kind !== SEAL_KIND) continue;

          // Decrypt seal
          const rumorJson = await user.signer.nip44.decrypt(seal.pubkey, seal.content);
          const rumor = JSON.parse(rumorJson) as NostrEvent;

          if (rumor.pubkey !== seal.pubkey) continue;
          if (rumor.kind !== DM_KIND) continue;

          // Get all participants from p tags
          const pTags = rumor.tags.filter(t => t[0] === 'p').map(t => t[1]);
          
          // Only include if this is a group (more than 1 p tag)
          if (pTags.length <= 1) continue;

          // Include sender in participants
          const allParticipants = [...new Set([rumor.pubkey, ...pTags])];
          const groupId = getGroupId(allParticipants);
          
          // Get subject if present
          const subject = rumor.tags.find(t => t[0] === 'subject')?.[1];

          const message: DirectMessage = {
            id: rumor.id,
            pubkey: rumor.pubkey,
            content: rumor.content,
            createdAt: rumor.created_at,
            conversationWith: groupId,
            participants: allParticipants,
            subject,
          };

          const existing = groupMap.get(groupId) || { 
            participants: allParticipants, 
            messages: [],
            subject 
          };
          existing.messages.push(message);
          // Update subject if newer message has one
          if (subject && message.createdAt > (existing.messages[0]?.createdAt || 0)) {
            existing.subject = subject;
          }
          groupMap.set(groupId, existing);

        } catch (error) {
          console.debug('[DM] Failed to decrypt group message:', error);
        }
      }

      // Build group conversation list
      const conversations: Conversation[] = [];

      for (const [groupId, data] of groupMap) {
        const sorted = data.messages.sort((a, b) => b.createdAt - a.createdAt);
        
        conversations.push({
          pubkey: groupId, // Use group ID as identifier
          participants: data.participants,
          lastMessage: sorted[0],
          unreadCount: 0,
          isGroup: true,
          subject: data.subject,
        });
      }

      // Sort by last message time
      return conversations.sort((a, b) => 
        (b.lastMessage?.createdAt || 0) - (a.lastMessage?.createdAt || 0)
      );
    },
    enabled: nip44Available,
    ...amberSafeQueryOptions(isInteractiveSigner(user, logins)),
  });
}

/**
 * Hook to fetch messages in a specific group conversation
 */
export function useGroupConversation(participants: string[]) {
  const { nostr } = useNostr();
  const { logins } = useNostrLogin();
  const { user } = useCurrentUser();
  const { config } = useAppContext();
  const nip44Available = checkNip44(user, logins);
  
  const groupId = getGroupId(participants);

  const relayUrls = config.relayMetadata.relays
    .filter(r => r.read)
    .map(r => r.url)
    .sort()
    .join(',');

  return useQuery({
    queryKey: ['group-messages', groupId, user?.pubkey, relayUrls],
    queryFn: async (c) => {
      if (!user?.signer?.nip44 || participants.length === 0) return [];

      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(15000)]);

      const readRelays = config.relayMetadata.relays
        .filter(r => r.read)
        .map(r => r.url);

      // Query for gift wraps addressed to us
      const allEvents: NostrEvent[] = [];

      for (const relayUrl of readRelays) {
        try {
          const relay = nostr.relay(relayUrl);
          const events = await relay.query(
            [{
              kinds: [GIFT_WRAP_KIND],
              '#p': [user.pubkey],
              limit: 200,
            }],
            { signal }
          );
          allEvents.push(...events);
        } catch (error) {
          console.warn(`[DM] Failed to query ${relayUrl}:`, error);
        }
      }

      // Deduplicate
      const uniqueEvents = Array.from(
        new Map(allEvents.map(e => [e.id, e])).values()
      );

      // Decrypt and filter to this group
      const messages: DirectMessage[] = [];

      for (const giftWrap of uniqueEvents) {
        try {
          const sealJson = await user.signer.nip44.decrypt(giftWrap.pubkey, giftWrap.content);
          const seal = JSON.parse(sealJson) as NostrEvent;

          if (seal.kind !== SEAL_KIND) continue;

          const rumorJson = await user.signer.nip44.decrypt(seal.pubkey, seal.content);
          const rumor = JSON.parse(rumorJson) as NostrEvent;

          if (rumor.pubkey !== seal.pubkey) continue;
          if (rumor.kind !== DM_KIND) continue;

          // Get all participants
          const pTags = rumor.tags.filter(t => t[0] === 'p').map(t => t[1]);
          const allParticipants = [...new Set([rumor.pubkey, ...pTags])];
          const messageGroupId = getGroupId(allParticipants);

          // Only include if this message is part of the target group
          if (messageGroupId !== groupId) continue;

          const subject = rumor.tags.find(t => t[0] === 'subject')?.[1];
          const replyTo = rumor.tags.find(t => t[0] === 'e')?.[1];

          messages.push({
            id: rumor.id,
            pubkey: rumor.pubkey,
            content: rumor.content,
            createdAt: rumor.created_at,
            conversationWith: groupId,
            participants: allParticipants,
            subject,
            replyTo,
          });

        } catch (error) {
          console.debug('[DM] Failed to decrypt:', error);
        }
      }

      // Sort by creation time (oldest first for chat display)
      return messages.sort((a, b) => a.createdAt - b.createdAt);
    },
    enabled: nip44Available && participants.length > 0,
    ...amberSafeQueryOptions(isInteractiveSigner(user, logins)),
  });
}
